import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import {
  Box,
  Divider,
  Menu,
  MenuItem as MuiMenuItem,
  Paper,
  Popper,
  Tooltip,
  useTheme,
} from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import diamonIcon from '@/assets/images/Icons-diamond.png'
import Button from '@/components/Button'
import Chip from '@/components/Chip'
import { useAppSelector } from '@/hooks/redux'
import { Features, Workflows } from '@/utils/constants'

export interface MenuOption {
  id: string | number
  label: React.ReactNode
  route?: string
  onClick?: () => void
  tooltip?: string
  badge?: string
  children?: MenuOption[]
  selected?: boolean
  key?: string
}

interface MenuItemProps {
  menuOption: MenuOption
  index?: number
  tooltipPlacement?: 'left' | 'right'
  onCloseMenu: () => void
  parentKey?: string
  renderDiamondIcon?: boolean
  disableTranslation?: boolean
}

const MenuItem: React.FC<MenuItemProps> = ({
  menuOption,
  index,
  tooltipPlacement = 'left',
  onCloseMenu,
  parentKey,
  renderDiamondIcon,
  disableTranslation = false,
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const location = useLocation()
  const { i18n } = useTranslation()

  const isSelected =
    menuOption.route === location.pathname + location.search ||
    menuOption.selected ||
    (parentKey === undefined &&
      disableTranslation &&
      menuOption.id === i18n.language)

  const activePlan = useAppSelector((state) => state.app.activePlan)
  const subscribedFeatures = activePlan?.features?.map(
    (x: { feature: { key: any } }) => x?.feature?.key,
  )
  const subscribedWorkflows = activePlan?.workflows?.map(
    (x: { workflow: { key: any } }) => x?.workflow?.key,
  )

  const isAccessible = (key: string) =>
    subscribedFeatures?.includes(Features[key as keyof typeof Features])

  const isAccessibleWorkflow = (key: string) =>
    subscribedWorkflows?.includes(Workflows[key as keyof typeof Workflows])

  const { t } = useTranslation('translation', {
    keyPrefix: 'Navbar.nestedMenu',
  })

  const { t: tooltipTranslate } = useTranslation('translation', {
    keyPrefix: 'Navbar.tooltip.nestedMenuTooltip',
  })
  const translate = menuOption.key
  return (
    <Tooltip
      title={menuOption.tooltip ? tooltipTranslate(menuOption.key) : ''}
      placement={tooltipPlacement}
      arrow
    >
      <MuiMenuItem
        sx={{
          width: '100%',
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          py: '6px',
          px: 2,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.10)',
          },
        }}
        selected={isSelected}
        onClick={() => {
          const isParent = !parentKey
          const mappedKey =
            menuOption.key === 'WASHBAKE'
              ? 'FRAMEWORK_STAINING'
              : menuOption.key

          const hasAccess = isParent
            ? isAccessible(mappedKey || '') ||
              menuOption.key === Features.SHADEGUIDEAI
            : ['STAINING_STUDIO', 'MATISSE_LITE'].includes(parentKey)
              ? isAccessible(
                  menuOption.key === 'WASHBAKE'
                    ? 'FRAMEWORK_STAINING'
                    : menuOption.key === 'STAIN_&_GLAZE'
                      ? 'STAINING_STUDIO'
                      : menuOption.key || '',
                )
              : isAccessibleWorkflow(parentKey)

          if (!hasAccess && renderDiamondIcon) {
            onCloseMenu()
            navigate('/plans')
            return
          }

          menuOption?.onClick?.()
          if (menuOption?.route) {
            navigate(menuOption.route)
          }
          onCloseMenu()
        }}
      >
        <>
          {index ? `${index}. ` : ''}
          {disableTranslation ? menuOption.label : t(`${translate}`)}
        </>

        {menuOption.badge && (
          <Chip
            label={menuOption.badge}
            size="small"
            variant="filled"
            color="primary"
            sx={{ ml: 'auto' }}
          />
        )}
        {renderDiamondIcon &&
          !parentKey &&
          !isAccessible(
            menuOption.key === Features.SHADEGUIDEAI
              ? 'analog_method'
              : menuOption.key === 'WASHBAKE'
                ? 'FRAMEWORK_STAINING'
                : menuOption.key || '',
          ) && (
            <img
              src={diamonIcon}
              alt="diamond icon"
              style={{ height: '16px', marginLeft: 'auto' }}
            />
          )}
        {renderDiamondIcon &&
          parentKey &&
          ((['STAINING_STUDIO', 'MATISSE_LITE'].includes(parentKey) &&
            !isAccessible(
              menuOption.key === 'WASHBAKE'
                ? 'FRAMEWORK_STAINING'
                : menuOption.key === 'STAIN_&_GLAZE'
                  ? 'STAINING_STUDIO'
                  : menuOption.key || '',
            )) ||
            (!['STAINING_STUDIO', 'MATISSE_LITE'].includes(parentKey) &&
              !isAccessibleWorkflow(parentKey))) && (
            <img
              src={diamonIcon}
              alt="diamond icon"
              style={{
                height: '16px',
                marginLeft: '8px',
              }}
            />
          )}
      </MuiMenuItem>
    </Tooltip>
  )
}

const NestedMenuItem: React.FC<MenuItemProps> = ({
  menuOption,
  onCloseMenu,
  renderDiamondIcon,
  disableTranslation = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const theme = useTheme()
  const location = useLocation()

  const activePlan = useAppSelector((state) => state.app.activePlan)
  const subscribedWorkflows = activePlan?.workflows?.map(
    (x: { workflow: { key: any } }) => x?.workflow?.key,
  )

  const isAccessibleWorkflow = (key: string) =>
    subscribedWorkflows?.includes(Workflows[key as keyof typeof Workflows])

  const isSelected =
    menuOption.children?.some(
      (child) => child.route === location.pathname || child.selected,
    ) || menuOption.selected

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMouseLeave = () => {
    setAnchorEl(null)
  }
  const { t } = useTranslation('translation', {
    keyPrefix: 'Navbar.navRecipes',
  })

  const { t: tooltipTranslate } = useTranslation('translation', {
    keyPrefix: 'Navbar.tooltip',
  })
  const translate = menuOption.key
  return (
    <MuiMenuItem
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        width: '100%',
        padding: 0,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(255, 255, 255, 0.10)',
        },
      }}
      selected={isSelected}
    >
      <Tooltip
        title={tooltipTranslate(menuOption.key) || ''}
        placement="left"
        arrow
        slotProps={{
          popper: {
            sx: {
              [`& .MuiTooltip-tooltip`]: {
                maxWidth: 280,
                whiteSpace: 'normal',
              },
            },
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          height="100%"
          px={2}
          py={'6px'}
        >
          {disableTranslation ? menuOption.label : t(`${translate}`)}
          <Box display="flex" alignItems="center" ml="auto">
            {menuOption.badge && (
              <Chip
                label={menuOption.badge}
                size="small"
                variant="filled"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}

            {renderDiamondIcon &&
              !isAccessibleWorkflow(menuOption.key || '') &&
              menuOption.label !== 'Staining Studio' &&
              menuOption.label !== 'Matisse Lite' && (
                <img
                  src={diamonIcon}
                  alt="diamond icon"
                  style={{ height: '16px', marginLeft: '8px' }}
                />
              )}

            <NavigateNextIcon fontSize="small" sx={{ ml: 1 }} />
          </Box>
        </Box>
      </Tooltip>

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="right-start"
        sx={{ zIndex: 1300 }}
        modifiers={[{ name: 'offset', options: { offset: [-8, 0] } }]}
      >
        <Paper
          elevation={2}
          sx={{
            backgroundColor:
              'linear-gradient(rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%), rgb(18, 18, 18)',
            color: theme.palette.text.primary,
            marginLeft: '0.25rem',
            paddingY: '0.5rem',
          }}
        >
          {menuOption.children?.map((child, index) => (
            <MenuItem
              key={child.id}
              menuOption={{
                ...child,
                onClick: () => {
                  child?.onClick?.()
                  onCloseMenu()
                },
              }}
              index={index + 1}
              tooltipPlacement="right"
              onCloseMenu={handleMouseLeave}
              parentKey={menuOption?.key}
              renderDiamondIcon={renderDiamondIcon}
            />
          ))}
        </Paper>
      </Popper>
    </MuiMenuItem>
  )
}

interface NavMenuProps {
  label: string | React.ReactNode
  menuOptions: MenuOption[]
  endIcon?: React.ReactNode
  buttonSx?: any
  renderDiamondIcon?: boolean
  disableTranslation?: boolean
}

const NavMenu: React.FC<NavMenuProps> = ({
  label,
  menuOptions,
  endIcon,
  buttonSx,
  renderDiamondIcon = true,
  disableTranslation = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleMenuToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const onCloseMenu = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <Button
        aria-controls={
          typeof label === 'string' ? label.toLowerCase() : 'nav-menu'
        }
        aria-haspopup="true"
        onClick={handleMenuToggle}
        endIcon={endIcon || <ExpandMoreIcon />}
        label={label}
        color="default"
        variant="text"
        size="large"
        sx={buttonSx}
      />

      <Menu
        id={typeof label === 'string' ? label.toLowerCase() : 'nav-menu'}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseMenu}
        sx={{
          '& .MuiPaper-root': {
            minWidth: '10rem',
            maxHeight: '28.125rem',
          },
          '& .MuiMenu-list': {
            paddingTop: '10px',
            paddingBottom: '10px',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {menuOptions.map((menuOption) => {
          if (menuOption.label === 'divider') {
            return (
              <Divider
                key={menuOption.id}
                sx={{
                  borderColor: 'rgba(255,255,255,0.1)',
                  marginY: 0.5,
                }}
              />
            )
          }

          return (
            <Box key={menuOption.id}>
              {menuOption.children ? (
                <NestedMenuItem
                  menuOption={menuOption}
                  onCloseMenu={onCloseMenu}
                  renderDiamondIcon={renderDiamondIcon}
                  disableTranslation={disableTranslation}
                />
              ) : (
                <MenuItem
                  menuOption={menuOption}
                  onCloseMenu={onCloseMenu}
                  renderDiamondIcon={renderDiamondIcon}
                  disableTranslation={disableTranslation}
                />
              )}
            </Box>
          )
        })}
      </Menu>
    </>
  )
}

export default NavMenu
